import { format, startOfDay } from "date-fns";
import React, { useEffect, useRef, useState } from "react";

import type {
	GetUserCalendarIntegrationStatusResponse,
	MeetingModelJson,
	UpdateMeetingRequestJson,
} from "@performyard/service-contracts";

import { CalendarErrorModal } from "./CalendarErrorModal";
import MeetingForm, { type MeetingFormData } from "./MeetingForm";
import { VideoConferencingErrorModal } from "./VideoConferencingErrorModal";

import { useEditWorkspaceMeeting } from "../../../../../api/hooks";
import useForm from "../../../../../hooks/useForm";
import { type SeriesEventOwnership, useCalendarIntegrationStatus, useSeriesEventOwnership } from "../../hooks";
import { useCalendarErrorHandler, useValidationErrorClearer, useVideoConferencingErrorHandler } from "./hooks";

import { type MeetingFormSchema, meetingFormSchema } from "./meeting-form-schema";
import { type MeetingFormValuesWithOptionalDates, getDefaultFormValues } from "./meeting-form-utils";

const CALENDAR_WILL_UPDATE_NOTIFICATION =
	"Any updates to this meeting will automatically update in your connected calendar.";
const CALENDAR_WILL_NOT_UPDATE_NOTIFICATION =
	"Any updates to this meeting won't update its calendar event because it isn't linked to your connected calendar.";

const willCalendarEventBeUpdated = (
	meeting: MeetingModelJson | null,
	userCalendarStatus: GetUserCalendarIntegrationStatusResponse | undefined,
	seriesEventOwnership: SeriesEventOwnership
): boolean => {
	if (!meeting?.calendarEvent || !userCalendarStatus) {
		return false;
	}

	const hasValidCalendarEvent =
		meeting.calendarEvent.eventId && meeting.calendarEvent.provider && meeting.calendarEvent.calendarId;

	if (!hasValidCalendarEvent) {
		return false;
	}

	const isCalendarIntegrationMatched =
		userCalendarStatus.isConnected &&
		userCalendarStatus.provider === meeting.calendarEvent.provider &&
		(seriesEventOwnership === "owner" ||
			userCalendarStatus.selectedCalendarId === meeting.calendarEvent.calendarId);

	return isCalendarIntegrationMatched;
};

/**
 * The sync notice for a meeting that has a calendar event: edits either flow to the
 * event (the editor's integration manages it) or silently skip it — series occurrences
 * only sync for the series' calendar owner — and the editor deserves to know which.
 */
export const calendarUpdateNotificationFor = (
	meeting: MeetingModelJson | null,
	userCalendarStatus: GetUserCalendarIntegrationStatusResponse | undefined,
	seriesEventOwnership: SeriesEventOwnership
): string | undefined => {
	if (!meeting?.calendarEvent?.eventId || !meeting.calendarEvent.provider || !meeting.calendarEvent.calendarId) {
		return undefined;
	}
	// No verdict until the status loads (it stays undefined when company calendar
	// access is off, matching the previous no-notice behavior) — a wrong notice is
	// worse than none.
	if (!userCalendarStatus) {
		return undefined;
	}
	if (seriesEventOwnership === "non-owner") {
		return CALENDAR_WILL_NOT_UPDATE_NOTIFICATION;
	}
	return willCalendarEventBeUpdated(meeting, userCalendarStatus, seriesEventOwnership)
		? CALENDAR_WILL_UPDATE_NOTIFICATION
		: CALENDAR_WILL_NOT_UPDATE_NOTIFICATION;
};

type EditMeetingModalProps = {
	isOpen: boolean;
	handleClose: () => void;
	meeting: MeetingModelJson | null;
};

export const EditMeetingModal = ({ isOpen, handleClose, meeting }: EditMeetingModalProps): React.ReactElement => {
	const [validationErrors, setValidationErrors] = useState<Partial<Record<keyof MeetingFormSchema, string>>>({});

	const calendarNotificationRef = useRef<string | undefined>(undefined);

	const { userCalendarStatus } = useCalendarIntegrationStatus();
	const seriesEventOwnership = useSeriesEventOwnership(meeting);
	const { modalState: calendarModalState, handleCalendarError, closeModal, setRetrying } = useCalendarErrorHandler();
	const {
		modalState: vcModalState,
		handleVideoConferencingError,
		closeModal: closeVcModal,
		setRetrying: setVcRetrying,
	} = useVideoConferencingErrorHandler();

	const { mutate: editWorkspaceMeeting, isPending } = useEditWorkspaceMeeting(handleClose, (error, variables) => {
		const context = {
			operation: "update" as const,
			meetingData: variables.data,
			meetingId: variables.meetingId,
		};
		if (handleVideoConferencingError(error, context)) return true;
		return handleCalendarError(error, context);
	});

	const {
		validate,
		updateForm,
		state: formValues,
		setNewDefaultValues,
	} = useForm({
		schema: meetingFormSchema,
		defaultValues: getDefaultFormValues(),
		disableWarning: true,
	});

	const initializedMeetingIdRef = useRef<string | null>(null);

	useEffect(() => {
		if (isOpen && meeting && initializedMeetingIdRef.current !== meeting._id) {
			const startDateTime = new Date(meeting.startDateTime);
			const endDateTime = new Date(meeting.endDateTime);

			setNewDefaultValues({
				...getDefaultFormValues(),
				name: meeting.name,
				startDate: startOfDay(startDateTime),
				endDate: startOfDay(endDateTime),
				startTime: format(startDateTime, "HH:mm"),
				endTime: format(endDateTime, "HH:mm"),
				description: meeting.description || "",
				carryOver: false,
				addToCalendar: false,
				videoConferencingProvider: null,
			});
			setValidationErrors({});
			initializedMeetingIdRef.current = meeting._id;

			calendarNotificationRef.current = calendarUpdateNotificationFor(
				meeting,
				userCalendarStatus,
				seriesEventOwnership
			);
		} else if (!isOpen) {
			initializedMeetingIdRef.current = null;
		}
	}, [isOpen, meeting, userCalendarStatus, seriesEventOwnership, setNewDefaultValues]);

	const calendarNotification = isOpen
		? calendarUpdateNotificationFor(meeting, userCalendarStatus, seriesEventOwnership)
		: calendarNotificationRef.current;

	// Cast to optional dates type since the form allows clearing dates during editing
	const formValuesWithOptionalDates = formValues as MeetingFormValuesWithOptionalDates;

	useValidationErrorClearer(formValuesWithOptionalDates, validationErrors, setValidationErrors);

	const handleSubmit = (data: MeetingFormData): void => {
		if (!meeting) return;

		const validationResult = validate();
		if (!validationResult.isValid) {
			const errors = Object.entries(validationResult.errors || {}).reduce(
				(acc, [key, error]) => {
					acc[key as keyof MeetingFormSchema] = error.message;
					return acc;
				},
				{} as Partial<Record<keyof MeetingFormSchema, string>>
			);
			setValidationErrors(errors);
			return;
		}

		// Strip form-only fields the BE doesn't accept on update. VC management mirrors
		// calendar — it happens through the row-menu/options dropdown, not this form.
		const { videoConferencingProvider: _drop, ...rest } = data;
		const formattedData: UpdateMeetingRequestJson = {
			...rest,
			startDateTime: data.startDateTime.toISOString(),
			endDateTime: data.endDateTime.toISOString(),
		};

		editWorkspaceMeeting({ meetingId: meeting._id, data: formattedData });
	};

	const onClose = (): void => {
		if (calendarModalState.type !== "none") {
			closeModal();
			return;
		}
		if (vcModalState.type !== "none") {
			closeVcModal();
			return;
		}

		setValidationErrors({});
		handleClose();
	};

	return (
		<>
			{calendarModalState.type === "none" && vcModalState.type === "none" && (
				<MeetingForm
					isOpen={isOpen}
					onClose={onClose}
					onSubmit={handleSubmit}
					formValues={formValuesWithOptionalDates}
					onFormChange={updateForm}
					validationErrors={validationErrors}
					submitLabel="Save Changes"
					isPending={isPending}
					title="Edit Meeting Details"
					showMeetingIntegrations={false}
					calendarUpdateNotification={calendarNotification}
				/>
			)}

			<VideoConferencingErrorModal
				modalState={vcModalState}
				onRetry={() => {
					if (vcModalState.type === "error") {
						setVcRetrying(true);
						const { context } = vcModalState;
						if (context.operation === "update") {
							editWorkspaceMeeting(
								{ meetingId: context.meetingId, data: context.meetingData },
								{
									onSuccess: () => closeVcModal(),
									onError: () => setVcRetrying(false),
								}
							);
						}
					}
				}}
				onContinue={() => {
					if (vcModalState.type === "error") {
						closeVcModal();
					}
				}}
				onClose={closeVcModal}
			/>

			<CalendarErrorModal
				modalState={calendarModalState}
				onRetry={() => {
					if (calendarModalState.type === "error") {
						setRetrying(true);
						const { context } = calendarModalState;
						if (context.operation === "update") {
							editWorkspaceMeeting(
								{ meetingId: context.meetingId, data: context.meetingData },
								{
									onSuccess: () => closeModal(),
									onError: () => setRetrying(false),
								}
							);
						}
					}
				}}
				onContinue={() => {
					if (calendarModalState.type === "error") {
						closeModal();
					}
				}}
				onClose={closeModal}
			/>
		</>
	);
};
